import * as React from 'react'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {connect} from 'react-redux'
import {ISidebarDispatcher} from '../../core/interfaces/dispatchers'
import {singIn, register, clearErrors} from '../../dispatchers/userInfo'
import {IPopupDispatcher} from '../../core/interfaces/dispatchers'
import {IAppStore, IUserInfo} from '../../interfaces/reducers'
import OverlayLoader from '../../core/components/Loading/OverlayLoading'
import {appPopupDispatcher} from '../../dispatchers/appPopupDispatcher'
import {appSidebarDispatcher} from '../../dispatchers/appSidebarDispatcher'
import appConfig from '../../reducers/appConfig'

interface OwnProps {
  userInfo: IUserInfo
}

interface DispatchProps extends ISidebarDispatcher, IPopupDispatcher {
  singIn: typeof singIn
  clearErrors: typeof clearErrors
  register: typeof register
}

interface ParentProps {}

type Props = ParentProps & DispatchProps & OwnProps

const isEmailValid = (mail: string): boolean =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(mail)

const Login: React.FC<Props> = props => {
  const {t} = useTranslation('login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [isRegistration, setIsRegistration] = useState(false)
  const [names, setNames] = useState('')
  const [confPass, setConfPass] = useState('')
  const [acceptPolicy, setAcceptPolicy] = useState(false)
  const [emailError, setEmailError] = useState(false)

  const canEnter = (): boolean => {
    const hasUserNameAndPass = !!username && !!password
    return isRegistration ? hasUserNameAndPass && !!names && !!confPass && acceptPolicy && confPass == password : true
  }

  const submitOnEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode == 13 && canEnter()) {
      props.singIn(username, password)
    }
  }

  if (isRegistration && props.userInfo.successRegistrationAwaitingConfirm) {
    return (
      <div className="success-div">
        <div>{t('registrationSuccessTitle')}</div>
        <div>{t('registrationSuccessBody')}</div>
      </div>
    )
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
      <OverlayLoader size="30px" show={props.userInfo.loading} />
      <input
        id="username"
        value={username}
        placeholder={appConfig.isArchImport ? t('usernamePlaceholderArch') : t('usernamePlaceholder')}
        onChange={e => {
          setUsername(e.target.value)
          setEmailError(false)
        }}
        onKeyUp={submitOnEnter}
        className="form-control login-input"
      />
      {isRegistration && emailError ? <div style={{color: 'red'}}>{t('invalidEmail')}</div> : null}
      {isRegistration ? (
        <input
          id="names"
          value={names}
          placeholder={t('fullName')}
          onChange={e => setNames(e.target.value)}
          onKeyUp={submitOnEnter}
          className="form-control login-input"
        />
      ) : null}
      <input
        id="password"
        value={password}
        placeholder={t('password')}
        onChange={e => setPassword(e.target.value)}
        onKeyUp={submitOnEnter}
        type="password"
        className="form-control login-input"
      />
      {isRegistration ? (
        <input
          id="confPass"
          value={confPass}
          type="password"
          placeholder={t('confirmPassword')}
          onChange={e => setConfPass(e.target.value)}
          onKeyUp={submitOnEnter}
          className="form-control login-input"
        />
      ) : null}
      {isRegistration && confPass && confPass != password ? (
        <div style={{color: 'red'}}>{t('passwordMismatch')}</div>
      ) : null}
      {isRegistration ? (
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <input
            style={{transform: 'scale(1.5)'}}
            type="checkbox"
            checked={acceptPolicy}
            onClick={() => setAcceptPolicy(!acceptPolicy)}
          />
          <div>
            {t('acceptPolicyPrefix')}{' '}
            <a href="https://terms.alfagis.bg/" target="_blank" rel="noreferrer">
              {t('policyLinkText')}
            </a>{' '}
          </div>
        </div>
      ) : null}

      {props.userInfo.signInError ? <div style={{color: 'red'}}>{props.userInfo.signInError.message}</div> : null}
      <button
        type="button"
        disabled={!canEnter()}
        className="button"
        style={{height: '30px'}}
        onClick={() => {
          if (isRegistration && !isEmailValid(username)) {
            setEmailError(true)
            return
          }
          if (isRegistration) {
            props.register(username, password, names, '')
          } else {
            props.singIn(username, password)
          }
        }}
      >
        {isRegistration ? t('register') : t('signIn')}
      </button>
      {appConfig.isArchImport ? (
        <a
          href="javascript:;"
          style={{marginTop: '5px'}}
          onClick={() => {
            props.clearErrors()
            setIsRegistration(!isRegistration)
            setUsername('')
            setNames('')
            setConfPass('')
            setPassword('')
            setAcceptPolicy(false)
            setEmailError(false)
          }}
        >
          {isRegistration ? t('switchToLogin') : t('switchToRegister')}
        </a>
      ) : null}
    </div>
  )
}

const mapStateToProps = (state: IAppStore) => ({
  userInfo: state.userInfo
})

export default connect<OwnProps, DispatchProps>(mapStateToProps, {
  ...appSidebarDispatcher,
  singIn,
  register,
  clearErrors,
  ...appPopupDispatcher
})(Login)
